/**
 * Zod schemas for all critical API response shapes.
 *
 * Strategy:
 * - `safeValidate(schema, data)` logs mismatches in development but never throws,
 *   so schema drift never breaks production for end users.
 * - Apply at API slice boundaries (thunks) so Redux state is always clean.
 */

import { z } from "zod";

// ─── Utility ──────────────────────────────────────────────────────────────────

/**
 * Validates `data` against `schema`.
 * - Returns the parsed (typed) value on success.
 * - In development, logs schema errors to console.
 * - Always returns the original data as a fallback so callers are never blocked.
 */
export function safeValidate<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  label?: string
): z.infer<T> {
  const result = schema.safeParse(data);
  if (!result.success && process.env.NODE_ENV === "development") {
    console.warn(
      `[Zod] Schema validation failed${label ? ` for "${label}"` : ""}:`,
      result.error.flatten()
    );
  }
  // Return parsed data if valid, fall back to raw data to avoid breaking the app
  return result.success ? result.data : (data as z.infer<T>);
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const UserCourseSchema = z.object({
  id: z.number(),
  student_id: z.number(),
  course_id: z.number(),
  status: z.string(),
});

export const UserOrganizationSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  name: z.string(),
  role: z.enum(["student", "tutor", "organization", "admin"]),
  address: z.string().optional(),
  userCourses: z.array(UserCourseSchema).default([]),
  organization: UserOrganizationSchema.optional(),
  accessToken: z.string().default(""),
  refreshToken: z.string().default(""),
});

export const AuthResponseSchema = z.object({
  user: UserSchema,
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  message: z.string().optional(),
  requiresEmailVerification: z.boolean().optional(),
  success: z.boolean().optional(),
  token: z.string().optional(),
});

// ─── Courses ──────────────────────────────────────────────────────────────────

export const ModuleSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  duration: z.string().optional(),
  lessons: z.array(z.any()).default([]),
});

export const CourseSchema = z.object({
  id: z.number(),
  title: z.string(),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  thumbnail: z.string().optional(),
  level: z.enum(["beginner", "intermediate", "advanced", "all-levels"]).optional(),
  category: z.string().optional(),
  isPublished: z.union([z.boolean(), z.string()]).optional(),
  enrolledStudents: z.number().optional(),
  organizationId: z.number().optional(),
  instructorName: z.string().optional(),
  teacher_id: z.number().optional(),
  price_amount: z.number().nullable().optional(),
  price_currency: z.string().optional(),
  modules: z.array(ModuleSchema).default([]),
  learning_outcomes: z.array(z.string()).default([]),
  languages: z.array(z.string()).default([]),
  certification: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
}).passthrough(); // allow extra fields from backend without breaking

export const PaginationSchema = z.object({
  total: z.number(),
  currentPage: z.number().optional(),
  totalPages: z.number().optional(),
});

export const FetchCoursesResponseSchema = z.object({
  data: z.object({
    courses: z.array(CourseSchema),
    pagination: PaginationSchema,
  }),
});

// ─── Schedules ────────────────────────────────────────────────────────────────

export const ScheduleSchema = z.object({
  id: z.number(),
  title: z.string().optional(),
  courseId: z.union([z.number(), z.string()]).optional(),
  course_id: z.union([z.number(), z.string()]).optional(),
  date: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  status: z.string().optional(),
}).passthrough();

export const FetchSchedulesResponseSchema = z.object({
  data: z.array(ScheduleSchema),
  pagination: PaginationSchema.optional(),
}).passthrough();

// ─── Tasks ────────────────────────────────────────────────────────────────────

export const TaskSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().optional(),
  courseId: z.union([z.number(), z.string()]).optional(),
  course_id: z.union([z.number(), z.string()]).optional(),
  dueDate: z.string().optional(),
  due_date: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
}).passthrough();

export const FetchTasksResponseSchema = z.object({
  data: z.array(TaskSchema),
}).passthrough();

// ─── Organization ─────────────────────────────────────────────────────────────

export const OrganizationSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().email().optional(),
  status: z.string().optional(),
  subscription: z.string().optional(),
}).passthrough();

// ─── Generic paginated response ───────────────────────────────────────────────

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    pagination: PaginationSchema.optional(),
    meta: z.object({
      total: z.number(),
      page: z.number(),
      limit: z.number(),
      totalPages: z.number(),
    }).optional(),
  }).passthrough();

// ─── Type exports (inferred from schemas) ─────────────────────────────────────

export type ValidatedUser = z.infer<typeof UserSchema>;
export type ValidatedCourse = z.infer<typeof CourseSchema>;
export type ValidatedSchedule = z.infer<typeof ScheduleSchema>;
export type ValidatedTask = z.infer<typeof TaskSchema>;
